import { has } from "lodash"
import { Iam } from "../models"
import { AssociationSettingsRepository } from "../repositories"
import { AssociationId, CohortName, NavMenuAccess, SystemFeaturesEnum, SystemOperationsEnum } from "../types"

export class IamService {
  identifyLoginAssociationCohort(
    preferrableAssociation: AssociationId,
    preferrableCohort: CohortName,
    allAssociationIamSettings: Record<AssociationId, Iam>,
    associationSettingsRepo: AssociationSettingsRepository,
  ) {
    let allAssociations = associationSettingsRepo.getAllAssociationIds()
    if (preferrableAssociation) {
      allAssociations = [...new Set([preferrableAssociation, ...allAssociations])]
    }
    function checkWorkforceAccess(associations: AssociationId[], allIamSettings: Record<AssociationId, Iam>) {
      for (const association of associations) {
        try {
          if (!allIamSettings[association]) {
            continue
          }
          let associationCohorts = associationSettingsRepo.getAssociationSettingsEntity(association).getAllCohorts()
          if (preferrableCohort) {
            associationCohorts = [...new Set([preferrableCohort, ...associationCohorts])]
          }
          for (const cohort of associationCohorts) {
            const hasCohortsAccess = allIamSettings[association].checkAccess(
              SystemFeaturesEnum.cohorts,
              /*
                We don't check a specific operation, as the web logs into any screen/page. 
                This is unlike the app where it logs directly into the profile listing page, 
                requiring us to check for listEmployeesInCohort or listCandidatesInCohort permission.
             */
              undefined,
              cohort,
            )
            if (hasCohortsAccess) {
              return { association, cohort }
            }
          }
        } catch (error) {
          console.log("Skipping association check: ", association)
        }
      }
      throw { code: "NoWorkforceAccess" }
    }

    function checkDocValidityAccess(associations: AssociationId[], allIamSettings: Record<AssociationId, Iam>) {
      for (const association of associations) {
        const hasDocValidityAccess = allIamSettings[association].checkAccess(
          SystemFeaturesEnum.reporting,
          SystemOperationsEnum.viewDocumentValidity,
        )
        if (hasDocValidityAccess) {
          return { association, cohort: "" }
        }
      }
      return null
    }

    const workforceAccessResult = checkWorkforceAccess(allAssociations, allAssociationIamSettings)
    if (workforceAccessResult) {
      return workforceAccessResult
    }

    const docValidityAccess = checkDocValidityAccess(allAssociations, allAssociationIamSettings)
    if (docValidityAccess) {
      return docValidityAccess
    }
    throw { code: "NoWorkforceAccess" }
  }

  buildNavMenuAccess(iamEntity: Iam, cohort: CohortName): NavMenuAccess {
    const hasDataLogsAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.cohorts,
      SystemOperationsEnum.viewProfileLogs,
      cohort,
    )
    const hasDeviceLogsAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.deviceManagement,
      SystemOperationsEnum.viewDeviceLogs,
    )
    const hasDocValidityAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.reporting,
      SystemOperationsEnum.viewDocumentValidity,
    )
    const hasDocumentLibraryAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.documentBuilder,
      SystemOperationsEnum.viewDocumentLibrary,
    )
    const hasDocExportAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.documents,
      SystemOperationsEnum.downloadBulkProfileDocuments,
    )
    const hasImportAccess = iamEntity.checkAccess(SystemFeaturesEnum.imports)
    const hasExportAccess = iamEntity.checkAccess(SystemFeaturesEnum.exports, SystemOperationsEnum.all)
    const hasRequestHandlerAccess = iamEntity.checkAccess(SystemFeaturesEnum.requests, SystemOperationsEnum.all)
    const hasUserManagementAccess = iamEntity.checkAccess(SystemFeaturesEnum.users, SystemOperationsEnum.all)
    const hasTrashAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.cohorts,
      SystemOperationsEnum.listTrashInCohort,
      cohort,
    )
    const hasEmployeesAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.cohorts,
      SystemOperationsEnum.listEmployeesInCohort,
      cohort,
    )
    const hasCandidatesAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.cohorts,
      SystemOperationsEnum.listCandidatesInCohort,
      cohort,
    )

    const hasFileAssociationAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.fileManagement,
      SystemOperationsEnum.assignFileToProfile,
    )
    const hasPerformanceImportAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.imports,
      SystemOperationsEnum.editPerformanceData,
    )

    const hasBulkTerminationAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.termination,
      SystemOperationsEnum.editTerminationBulk,
    )
    const hasBulkProfileImportAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.imports,
      SystemOperationsEnum.editProfilesBulk,
    )
    const hasBulkDisciplineAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.discipline,
      SystemOperationsEnum.editDisciplineBulk,
    )
    const hasDisciplineReportAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.discipline,
      SystemOperationsEnum.viewDisciplineReport,
    )
    const hasDataFlowConfigAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.systemConfiguration,
      SystemOperationsEnum.editDataFlowConfig,
    )
    const hasFieldConfigAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.systemConfiguration,
      SystemOperationsEnum.editFieldConfig,
    )
    const hasProcessConfigAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.systemConfiguration,
      SystemOperationsEnum.editProcessConfig,
    )
    const hasConfigUploadAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.systemConfiguration,
      SystemOperationsEnum.uploadConfigFiles,
    )
    const hasJsonConfigFileAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.systemConfiguration,
      SystemOperationsEnum.editJsonConfigFiles,
    )
    const hasBillingAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.billing,
      SystemOperationsEnum.viewBillingDashboard,
    )
    const hasGroupAccess = iamEntity.checkAccess(SystemFeaturesEnum.groups, SystemOperationsEnum.all)
    const hasPpeStockConfigAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.ppe,
      SystemOperationsEnum.editPpeStockConfig,
    )
    const hasPpeStockAccess = iamEntity.checkAccess(SystemFeaturesEnum.ppe, SystemOperationsEnum.editPpeStock)
    const hasPpeRequestAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.ppe,
      SystemOperationsEnum.createPpeIssueRequest,
    )
    const hasAdvancedDataQueryAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.advancedDataQueries,
      SystemOperationsEnum.viewDataQueries,
    )
    const hasModernApiAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.viewModernApi,
    )
    const hasModernApiSyncTriggerAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.triggerModernApiSync,
    )
    const hasAdaginAccess = iamEntity.checkAccess(SystemFeaturesEnum.integrations, SystemOperationsEnum.viewAdagin)
    const hasAgrigisticsAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.viewAgigistics,
    )
    const hasPaySpaceAccess = iamEntity.checkAccess(SystemFeaturesEnum.integrations, SystemOperationsEnum.viewPayspace)
    const hasAdaginSyncTriggerAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.triggerAdaginSync,
    )
    const hasAgrigisticsSyncTriggerAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.triggerAgrigisticsSync,
    )
    const hasPaySpaceSyncTriggerAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.triggerPayspaceSync,
    )
    const hasAdaginLogDownloadAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.viewAdaginLogs,
    )
    const hasAgrigisticsLogDownloadAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.viewAgrigisticsLogs,
    )
    const hasPaySpaceLogDownloadAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.viewPayspaceLogs,
    )
    const hasAdaginSourceFilesAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.viewAdaginSourceFiles,
    )
    const hasAgrigisticsSourceFilesAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.viewAgrigisticsSourceFiles,
    )
    const hasPaySpaceSourceFilesAccess = iamEntity.checkAccess(
      SystemFeaturesEnum.integrations,
      SystemOperationsEnum.viewPayspaceSourceFiles,
    )

    return {
      hasDataLogsAccess,
      hasDeviceLogsAccess,
      hasDocExportAccess,
      hasDocValidityAccess,
      hasDocumentLibraryAccess,
      hasImportAccess,
      hasExportAccess,
      hasRequestHandlerAccess,
      hasUserManagementAccess,
      hasCandidatesAccess,
      hasEmployeesAccess,
      hasTrashAccess,
      hasBulkDisciplineAccess,
      hasDisciplineReportAccess,
      hasBulkTerminationAccess,
      hasBulkProfileImportAccess,
      hasDataFlowConfigAccess,
      hasFieldConfigAccess,
      hasProcessConfigAccess,
      hasConfigUploadAccess,
      hasJsonConfigFileAccess,
      hasBillingAccess,
      hasGroupAccess,
      hasPpeStockAccess,
      hasPpeStockConfigAccess,
      hasPpeRequestAccess,
      hasAdvancedDataQueryAccess,
      hasModernApiAccess,
      hasModernApiSyncTriggerAccess,
      hasAdaginAccess,
      hasAgrigisticsAccess,
      hasPaySpaceAccess,
      hasAdaginSyncTriggerAccess,
      hasAgrigisticsSyncTriggerAccess,
      hasPaySpaceSyncTriggerAccess,
      hasAdaginLogDownloadAccess,
      hasAgrigisticsLogDownloadAccess,
      hasPaySpaceLogDownloadAccess,
      hasAdaginSourceFilesAccess,
      hasAgrigisticsSourceFilesAccess,
      hasPaySpaceSourceFilesAccess,
      hasFileAssociationAccess,
      hasPerformanceImportAccess,
    }
  }
}
